import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, FormControl, FormLabel } from '@chakra-ui/core'
import { Subtask } from 'constants/interfaces'
import { TextInput } from 'components/ui'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'
import { ExecutionSubtaskCard } from 'components/TasksV2/TasksOverview/components/ExecutionSubtaskCard'
import { useCreationText } from 'components/TasksV2/TaskCreation/hooks/useCreationText'
import { useTranslateDefaultTitle } from 'components/TasksV2/hooks/useTranslateDefaultTitle'
import keys from 'constants/keys'
import useLocaleText from 'components/useLocaleText'
import styles from './AIImageSubtaskPreview.module.css'

interface IProps {
  subtaskPath: string
}

const AIImageSubtaskWidget: React.FC<IProps> = ({ subtaskPath }) => {
  const { control, errors, watch } = useFormContext()
  const t = useCreationText()
  const previewLabel = useLocaleText('ai_team_will_see_this')
  const validationMessage = useLocaleText('t_img_st_error')
  const defaultTitle = useTranslateDefaultTitle('t_take_a_photo')
  const titlePath = `${subtaskPath}.title`
  const title = watch(titlePath, defaultTitle)
  const error = errors.sub_tasks?.[0]?.title
  // This read-only presentation draft never enters the execution or submission pipeline.
  const previewSubtask = {
    st_id: 'ai-subtask-preview',
    type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
    title: title || t.untitled,
    is_ai_enhanced: true,
    is_required: true,
  } as Subtask

  return (
    <section className={styles.preview}>
      <FormControl isRequired isInvalid={!!error}>
        <FormLabel htmlFor={`${titlePath}-input`} fontSize="13px" fontWeight={600}>
          {t.subtaskTitle}
        </FormLabel>
        <Controller
          as={TextInput}
          id={`${titlePath}-input`}
          name={titlePath}
          control={control}
          defaultValue={defaultTitle}
          elementHeight="40px"
          isInvalid={!!error}
          rules={{ required: validationMessage, validate: (value) => !!value?.trim() || validationMessage }}
        />
        {error && <ErrorMessage errorMessage={validationMessage} />}
      </FormControl>
      <div className={styles.card}>
        <p>{previewLabel}</p>
        <ExecutionSubtaskCard subtask={previewSubtask} selected={false} disabled readOnly onSelect={() => {}} />
      </div>
      <Controller
        name={`${subtaskPath}.type`}
        control={control}
        as={<Box />}
        defaultValue={keys.SUBTASK_TYPES.IMAGE_SUBTASK}
      />
    </section>
  )
}

export default AIImageSubtaskWidget
